import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategoryParam } from '../../hooks/useCategoryParam';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Plus, Minus, Search, Filter, Grid3X3, List, Sparkles, ShoppingBag, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { ProductCard } from '../../components/customer/ProductCard';
import { saveLoginIntent } from '../../utils/loginIntent';
import { storeOffersSubscriptions } from '../../utils/subscriptionEligibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProducts = () => {
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity, totalItems, totalAmount } = useCart();
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // Category lives in the URL, not component state: opening a product and pressing
  // back remounts this page, and local state was reset to null — dropping the
  // customer back at "all products" instead of the category they came from.
  const [selectedCategory, setSelectedCategory] = useCategoryParam();
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [storeSettings?.admin_id]);

  const loadData = async () => {
    try {
      const adminId = user?.admin_id || storeSettings?.admin_id;
      let catRes, prodRes;
      if (user && user.admin_id) {
        [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/admin/categories`),
          axios.get(`${API}/customer/products`)
        ]);
      } else if (adminId) {
        [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/public/store/${adminId}/categories`),
          axios.get(`${API}/public/store/${adminId}/products`)
        ]);
      } else {
        setLoading(false);
        return;
      }
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = (catId) => {
    // Merges into the existing query string, so ?tenant= (native query-param
    // mode) survives — the previous version replaced the whole query string.
    setSelectedCategory(catId);
    setSelectedSubCategories([]);
  };

  const autoPlay = storeSettings?.auto_image_carousel !== false;
  const detailEnabled = storeSettings?.product_detail_page !== false;

  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    const matchesSubCategory = selectedSubCategories.length === 0 || selectedSubCategories.includes(p.sub_category_id);
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  const handleAddToCart = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label} to cart`);
  };

  // "Subscribe" on a card is offered only where it can really be used: the
  // store has subscriptions switched on (the same flag that shows the
  // Subscribe tab) and the store marked that product as available for
  // subscription — the rule the server enforces too. When any listed product
  // qualifies, every card keeps the row's space so the cards stay the same height.
  const isSubscriptionsEnabled = storeOffersSubscriptions(storeSettings);
  const reserveSubscribe = isSubscriptionsEnabled && filteredProducts.some(p => p.is_subscription_available);

  // Opens the existing subscription setup with this product already chosen.
  const handleSubscribe = (product) => {
    const setup = `/subscriptions?product=${product.id}`;
    if (!user) {
      saveLoginIntent({ returnTo: setup });
      navigate('/login');
      toast.info('Please log in to subscribe.');
      return;
    }
    navigate(setup);
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';

  return (
    <CustomerLayout title="Products">
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        {/* Search Bar */}
        <div className="px-4 py-3 bg-[var(--theme-surface)] border-b border-black/5 sticky top-[60px] z-30">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 input-modern"
                data-testid="product-search-input"
              />
            </div>
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-black/5 bg-white"
              data-testid="view-mode-toggle"
            >
              {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid3X3 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Category Sidebar */}
          <div className="w-20 md:w-24 bg-[var(--theme-surface)] border-r border-black/5 overflow-y-auto scrollbar-thin">
            <div className="py-2">
              <button
                onClick={() => handleSelectCategory(null)}
                className={`w-full py-3 px-2 flex flex-col items-center gap-1 border-l-4 transition-all ${
                  !selectedCategory 
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' 
                    : 'border-transparent hover:bg-gray-50'
                }`}
                data-testid="sidebar-category-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  !selectedCategory ? 'bg-[var(--theme-primary)] text-white' : 'bg-gray-100'
                }`}>
                  <Sparkles className="w-6 h-6" />
                </div>
                <span className={`text-xs font-medium text-center ${
                  !selectedCategory ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'
                }`}>All</span>
              </button>
              
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleSelectCategory(category.id)}
                  className={`w-full py-3 px-2 flex flex-col items-center gap-1 border-l-4 transition-all ${
                    selectedCategory === category.id 
                      ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' 
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                  data-testid={`sidebar-category-${category.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl overflow-hidden ${
                    selectedCategory === category.id ? 'ring-2 ring-[var(--theme-primary)]' : ''
                  }`}>
                    {category.image_url ? (
                      <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xl font-bold text-[var(--theme-text-muted)]">
                        {category.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <span className={`text-xs font-medium text-center line-clamp-2 ${
                    selectedCategory === category.id ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'
                  }`}>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Subcategory Filter */}
            <SubCategoryFilter
              categories={categories}
              selectedTab={selectedCategory}
              selectedSubCategories={selectedSubCategories}
              onSelect={setSelectedSubCategories}
            />
            {loading ? (
              <div className={`${viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4' : 'space-y-3'}`}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="product-card p-3 animate-pulse">
                    <div className="aspect-square bg-gray-200 rounded-xl mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Filter className="w-16 h-16 text-[var(--theme-text-muted)] mx-auto mb-4 opacity-50" />
                <p className="text-[var(--theme-text-muted)]">No products found</p>
                <p className="text-sm text-[var(--theme-text-muted)] mt-1">Try adjusting your search or category</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {filteredProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index}
                    currencySymbol={currencySymbol} storeSettings={storeSettings}
                    getItemQuantity={getItemQuantity} handleAddToCart={handleAddToCart}
                    updateQuantity={updateQuantity} autoPlay={autoPlay}
                    detailEnabled={detailEnabled} navigate={navigate}
                    reserveSubscribe={reserveSubscribe} onSubscribe={handleSubscribe} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index}
                    currencySymbol={currencySymbol} storeSettings={storeSettings}
                    getItemQuantity={getItemQuantity} handleAddToCart={handleAddToCart}
                    updateQuantity={updateQuantity} autoPlay={autoPlay}
                    detailEnabled={detailEnabled} navigate={navigate}
                    reserveSubscribe={reserveSubscribe} onSubscribe={handleSubscribe} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reserves scroll space for the floating cart bar below (which is `fixed` and
          so doesn't occupy flow height on its own) — without this the last product
          card ends up partially hidden behind it. */}
      {totalItems > 0 && <div className="h-16" aria-hidden="true" />}

      {/* Floating Cart Bar */}
      {totalItems > 0 && (
        <div className="fixed left-0 right-0 z-40 px-4 pb-2" style={{ bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }} data-testid="products-floating-cart">
          <button
            onClick={() => navigate('/cart')}
            className="w-full flex items-center justify-between bg-[var(--theme-primary)] text-white rounded-2xl px-5 py-3.5 shadow-lg"
          >
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              <span className="font-bold">{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
              <span className="opacity-80">|</span>
              <span className="font-bold">{storeSettings?.currency_symbol || '₹'}{totalAmount.toFixed(0)}</span>
            </div>
            <div className="flex items-center gap-1 font-semibold">
              View Cart <ChevronRight className="w-4 h-4" />
            </div>
          </button>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerProducts;
